import { DependencyList, EffectCallback, useEffect, useRef, useState } from 'react'
import { LocalStorage, storage } from './local-storage'
import { Atom, WritableAtom } from 'jotai'
import { useAtom } from 'jotai'
import { WritableDraft } from 'immer/dist/internal'
import { useHydrateAtoms } from 'jotai/utils'

/** `useEffect` that receives an abort signal on re-render. */
export const useEffectWithAbortSignal = (
  effect: (abortSignal: AbortSignal) => void | (() => void),
  deps?: DependencyList
) => {
  useEffect(() => {
    const abortController = new AbortController()
    const effectDestructor = effect(abortController.signal)

    return () => {
      abortController.abort()
      if (typeof effectDestructor === 'function') {
        effectDestructor()
      }
    }
  }, deps)
}

/** `useEffect` that only runs on updates. */
export const useEffectOnUpdate = (effect: EffectCallback, deps?: DependencyList) => {
  const mounted = useRef(0)

  useEffect(() => {
    if (mounted.current > (process.env.NODE_ENV === 'development' ? 1 : 0)) {
      return effect()
    }
    mounted.current++
  }, deps)
}

/** Forces a re-render. */
export const useForceRender = () => {
  const [_, setSignal] = useState(false)

  return () => setSignal((signal) => !signal)
}

/** Returns what the value was on the previous render */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>()
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

export const useStateFromLocalStorage = <T extends keyof LocalStorage>(
  key: T,
  initialValue: LocalStorage[T],
  deps: DependencyList = []
) => {
  const [value, setValue] = useState<LocalStorage[T]>(initialValue)

  useEffect(() => {
    const item = storage.get(key)
    if (item) {
      setValue(item)
      return
    }
    storage.set(key, initialValue)
  }, deps)

  const wrappedSetValue = (
    value: LocalStorage[T] | ((previousValue: LocalStorage[T]) => LocalStorage[T])
  ) => {
    if (typeof value === 'function') {
      setValue((previousValue) => {
        const newValue = value(previousValue)
        storage.set(key, newValue)
        return newValue
      })
    } else {
      setValue(value)
      storage.set(key, value)
    }
  }

  return [value, wrappedSetValue] as const
}

/**
 * `useAtom` with hydration and initial value updates if it's changed.
 * Do not delete if returns are unused, has side effects (updating atom for other consumers).
 */
export const useAtomWithHydration = <T>(
  atom: WritableAtom<T, [T | ((draft: WritableDraft<T>) => void)], void>,
  initialValue: T
) => {
  useHydrateAtoms([[atom, initialValue]])
  const [value, setValue] = useAtom(atom)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  return [value, setValue] as const
}
